(async () => {
    const { Core } = await importModule('Core');

    const CN_ACTIVE = '!active';

    // How far above whatever's found in the corner to sit, and a cap on how
    // far up the button will ever move - a chat widget's window can grow to
    // nearly full-height once someone opens it, and chasing that would fling
    // the button somewhere useless rather than just sitting a sensible
    // distance above its closed bubble.
    const CORNER_GAP = -6;
    const MAX_CLEARANCE = 96;
    const PROBE_INTERVAL = 500;
    const MAX_PROBES = 60; // ~30s of polling before giving up

    customElements.define('scroll-to-top-button', class extends Core {
        elements = {
            trigger: '[data-trigger]',
            button: '[data-button]'
        }

        render() {
            this._handleTrigger();
            this._handleClick();
            this._avoidCornerWidget();
        }

        _handleTrigger() {
            const observer = new IntersectionObserver((entries) => {
                this.active = !entries[0].isIntersecting;
            });
            observer.observe(this.$trigger);
        }

        _handleClick() {
            this.addEventListener('click', this._onClick.bind(this));
        }

        _onClick() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        // Third-party widgets that sit fixed in the bottom-right corner
        // (tawk.to's chat bubble being the one on this site) load their own
        // markup asynchronously and don't share a stable, documented
        // selector to watch for - their bubble can be plain divs/images
        // rather than an iframe until someone actually opens the chat
        // window, so guessing at their DOM structure is fragile. Instead
        // this periodically samples what's actually sitting in that corner
        // of the viewport (ignoring this button's own element) and, if it
        // finds something else fixed/sticky there, matches this button's
        // size to it and lines the two up on the same horizontal center,
        // stacked with a small gap rather than each keeping its own fixed
        // size/offset and hoping they happen to line up.
        _avoidCornerWidget() {
            let probes = 0;

            const probe = () => {
                probes++;
                const widget = this._findCornerWidget();
                if (widget) {
                    this._trackCornerWidget(widget);
                    return;
                }
                if (probes < MAX_PROBES) {
                    setTimeout(probe, PROBE_INTERVAL);
                }
            };

            probe();
        }

        _findCornerWidget() {
            // A couple of centimetres in from the very corner - inside
            // where a chat bubble normally sits, whatever its exact margin.
            const x = window.innerWidth - 28;
            const y = window.innerHeight - 28;
            const stack = document.elementsFromPoint(x, y);

            const candidates = stack.filter(($el) => {
                if ($el === this || this.contains($el)) return false;
                const style = getComputedStyle($el);
                if (style.position !== 'fixed' && style.position !== 'sticky') return false;
                const rect = $el.getBoundingClientRect();
                if (!rect.width || !rect.height) return false;
                // Must actually occupy the bottom-right corner itself, not
                // just happen to be the element sampled at that point (e.g.
                // a full-page overlay).
                return rect.right > window.innerWidth - 220 && rect.bottom > window.innerHeight - 220;
            });

            if (!candidates.length) return null;

            // A chat widget's actual round bubble is usually nested inside
            // a larger positioning wrapper (which can cover a much bigger
            // area than what's visibly drawn, e.g. room for an expanded
            // chat panel), and both can land in the same point sample.
            // Picking the smallest match rather than just the first favors
            // the actual visible button over its wrapper.
            return candidates.reduce((smallest, $el) => {
                const rect = $el.getBoundingClientRect();
                const smallestRect = smallest.getBoundingClientRect();
                return rect.width * rect.height < smallestRect.width * smallestRect.height ? $el : smallest;
            });
        }

        _trackCornerWidget($widget) {
            const update = () => {
                const rect = $widget.getBoundingClientRect();
                if (!rect.height || !rect.width) return;

                // Match this button's own footprint to the widget's rather
                // than keeping its own fixed size, so the two read as a
                // matched pair - but pulled in a bit smaller than the
                // widget itself so it reads as the secondary control of the
                // pair. Clamped to a sane, compact bubble-icon range in
                // case what got matched is still a wrapper rather than the
                // visible button itself.
                const size = Math.min(Math.max(Math.max(rect.width, rect.height) * 0.82, 34), 50);
                this.$button.style.setProperty('--corner-size', `${size}px`);

                const raised = Math.max(0, window.innerHeight - rect.top);
                const clearance = Math.min(Math.max(raised + CORNER_GAP, 0), MAX_CLEARANCE);
                this.$button.style.setProperty('--corner-clearance', `${clearance}px`);

                // Center this button on the same horizontal point as the
                // widget instead of each independently hugging the right
                // edge with its own margin.
                const centerX = rect.left + rect.width / 2;
                const right = Math.max(window.innerWidth - centerX - size / 2, 0);
                this.$button.style.setProperty('--corner-right', `${right}px`);
            };

            update();
            window.addEventListener('resize', update);
            if (window.ResizeObserver) {
                new ResizeObserver(update).observe($widget);
            }
        }

        set active(state) {
            this.$button.classList.toggle(CN_ACTIVE, state);
        }
    });
})();
